import type { OpeningHoursInfo, OpeningHoursInfoStateEnum } from "@finn-no/mobility-dealer-types";
import { Trans, useLingui } from "@lingui/react";
import { WIcon } from "@mobility-item-page/client";
import { Badge } from "@warp-ds/react";
import { useEffect, useRef, useState } from "react";
import { ScreenReaderSeparator } from "./ScreenReaderSeparator.tsx";
import "./opening-hours.css";

export const OpeningHours = ({
  className = "",
  openingHoursInfo,
  startOpen = false,
  trackClick,
}: {
  className?: string;
  openingHoursInfo: OpeningHoursInfo | undefined;
  startOpen?: boolean;
  trackClick: () => void;
}) => {
  const { i18n } = useLingui();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [expanded, setExpanded] = useState(startOpen);

  useEffect(() => {
    const detailsElement = detailsRef.current;
    const listener = () => {
      setExpanded(() => {
        if (detailsElement?.open) {
          trackClick();
        }
        return detailsElement?.open ?? false;
      });
    };
    detailsElement?.addEventListener("toggle", listener);

    return () => {
      detailsElement?.removeEventListener("toggle", listener);
    };
  }, [trackClick]);

  if (!openingHoursInfo) {
    return null;
  }

  const { openingsHours, state, stateText, activeOpeningHours } = openingHoursInfo;

  return (
    <div className={className}>
      <h3 className="mb-16">
        <Trans id="dealer.dealerOverview.openingHours" message="Opening hours" />
      </h3>
      <details ref={detailsRef} open={expanded}>
        <summary className="flex gap-16 cursor-pointer [&::-webkit-details-marker]:hidden">
          <Badge variant={getBadgeVariantFromState(state)}>
            <span>{[stateText, activeOpeningHours].filter(Boolean).join(" · ")}</span>
          </Badge>
          <ScreenReaderSeparator />
          <div className="inline-flex items-center gap-8">
            <div>
              {i18n.t({
                id: "openingHoursToggle",
                message: `{expanded, select, true {Hide}false {Show}} opening hours`,
                values: { expanded },
              })}
            </div>
            <ScreenReaderSeparator />
            <WIcon name="ChevronUp" className={expanded ? "" : "rotate-180"} />
          </div>
        </summary>
        <ul>
          {openingsHours.map((day) => (
            <li
              key={day.days}
              className={`flex justify-between border-b last-of-type:border-none py-8 ${day.active ? "font-bold" : ""}`}
            >
              <div>{day.days}</div>
              <div>{day.openTimeRange}</div>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
};

const getBadgeVariantFromState = (state: OpeningHoursInfoStateEnum) => {
  switch (state) {
    case "Open":
      return "positive";
    case "Closed":
      return "negative";
    case "ClosingSoon":
      return "warning";
    default:
      return "info";
  }
};
